import cn from 'classnames'
import BaseFlyout from 'ext-ui/components/BaseFlyout/BaseFlyout'
import DigitalCreditCardFlyoutSvg from 'ext-ui/svg/illustration/DigitalCreditCardFlyoutSvg'
import React, { ReactElement, ReactNode } from 'react'
import SvgTooltip from 'src/components/Svgs/SvgTooltip'

import style, { flyoutStyle } from './SettingsDrawerShell.style'

type SettingsDrawerShellProps = {
  isOpen: boolean
  onClose: () => void
  /** Also doubles as the flyout's accessible name. */
  title: string
  /**
   * Opening copy for the card. A drawer whose lead needs more than one
   * sentence-group may pass several paragraphs rather than one string.
   */
  lead: ReactNode
  /** Closing ⓘ note. Omitted entirely when a drawer has none. */
  footnote?: ReactNode
  /**
   * Primary action for drawers that are a true form submission (spend limit,
   * billing address). It flows *after* the content card rather than sitting in
   * a sticky viewport-bottom rail, so a short form simply leaves the tinted
   * space below it empty. Drawers whose edits apply instantly have no submit
   * step and pass nothing.
   *
   * The action renders outside the card, so a button that submits a form
   * inside it needs `form={id}` rather than relying on containment.
   */
  action?: ReactNode
  children: ReactNode
  className?: string
}

/**
 * Shared shell for the Controls & Settings flyout drawers.
 *
 * Every editing flyout in the tab uses this one shell, which adapts the legacy
 * "update virtual card" action-flyout treatment: a white header band carrying
 * the close control, the title, and a clipped card-art corner; a tinted body;
 * and the drawer's own content inside a single white card.
 *
 * The general half of that pattern lives in the primitive — `BaseFlyout`
 * already owns the header band, the close control, the `decoration` slot and
 * the tinted body. What lives here is only the settings-scoped composition:
 * the card-art choice, the contained content card, and the flowing action.
 *
 * The header is title-only, so each drawer's lead copy sits at the top of the
 * card instead of in the band. Sections inside `children` that need a hairline
 * divider should use `SettingsDrawerSection` below.
 */
export default function SettingsDrawerShell(
  props: SettingsDrawerShellProps,
): ReactElement {
  const {
    isOpen,
    onClose,
    title,
    lead,
    footnote,
    action,
    children,
    className,
  } = props

  return (
    <BaseFlyout
      isOpen={isOpen}
      onClose={onClose}
      className={cn('SettingsDrawerShell', className)}
      css={flyoutStyle}
      aria-label={title}
      title={title}
      decoration={<DigitalCreditCardFlyoutSvg />}
    >
      <div className="SettingsDrawerShell-body" css={style}>
        <div className="SettingsDrawerShell-card">
          <div className="SettingsDrawerShell-lead">{lead}</div>
          {children}
          {footnote !== undefined && (
            <p className="SettingsDrawerShell-footnote">
              <SvgTooltip
                width={14}
                height={14}
                viewBox="0 0 24 24"
                aria-hidden="true"
              />
              {footnote}
            </p>
          )}
        </div>
        {action !== undefined && (
          <div className="SettingsDrawerShell-action">{action}</div>
        )}
      </div>
    </BaseFlyout>
  )
}

/** Hairline-divided section wrapper for drawer body content. */
export function SettingsDrawerSection(props: {
  children: ReactNode
}): ReactElement {
  return <div className="SettingsDrawerShell-section">{props.children}</div>
}
